import React, { useEffect, useState } from 'react';
import { FaPhone, FaPhoneSlash, FaTimes, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { MdPhoneLocked } from 'react-icons/md';
import { BsFillTelephoneMinusFill } from 'react-icons/bs';

const TERMINAL_STATUSES = new Set(['ended', 'disconnected', 'failed', 'rejected']);

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm} min, ${ss} sec`;
};

const STATUS_LABEL = {
  ringing: 'Incoming call…',
  connecting: 'Connecting…',
  connected: 'Connected',
  disconnecting: 'Ending…',
  ended: 'Call ended',
  rejected: 'Rejected',
  disconnected: 'Disconnected',
  failed: 'Call failed'
};

const STATUS_COLOR = {
  ringing: '#f59e0b',
  connecting: '#6b7280',
  connected: '#16a34a',
  disconnecting: '#6b7280',
  ended: '#6b7280',
  rejected: '#6b7280',
  disconnected: '#6b7280',
  failed: '#dc2626'
};

function IncomingCallPill({
  fromNumber,
  studentName,
  status,
  muted,
  // Multi-agent / conference state. All optional — pre-feature pills work as before.
  unlocked = false, // true once the call has been moved into a Conference
  isOriginator = true, // true on the pill of the ops user who answered/initiated
  participantCount = 1, // number of ops users currently in the conference (cms_*)
  onAccept,
  onReject,
  onToggleMute,
  onEndCall, // pre-unlock: disconnect SDK call (today's behavior)
  onUnlock, // originator only: POST /unlock_call to upgrade to conference
  onLeave, // post-unlock + 2+ ops: disconnect own leg, conference continues
  onEndForEveryone, // post-unlock: POST /end_ongoing then disconnect
  onClose
}) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    // Reset the timer whenever a fresh call cycle begins. Without this the
    // pill — which can outlive a single call if the agent hasn't clicked the
    // close button when the next call comes in — would continue from the
    // previous call's elapsed value once the new one connects.
    if (status === 'ringing' || status === 'connecting') {
      setElapsed(0);
    }
    if (status !== 'connected') return undefined;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const statusLabel = STATUS_LABEL[status] || status;
  const statusColor = STATUS_COLOR[status] || '#6b7280';

  return (
    <div
      style={{
        position: 'fixed',
        top: '90px',
        right: '20px',
        zIndex: 9999,
        background: 'var(--surface-card)',
        borderRadius: '9999px',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        padding: '8px 8px 8px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        border: '1px solid #e5e7eb',
        minWidth: '340px'
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: '6px' }}>
        <span style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}>
          {studentName || fromNumber || 'Unknown caller'}
        </span>
        {studentName && fromNumber && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>{fromNumber}</span>
        )}
        <span
          style={{
            fontSize: '12px',
            color: statusColor,
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '6px',
              height: '6px',
              borderRadius: '9999px',
              background: statusColor
            }}
          />
          {statusLabel}
          {status === 'connected' && (
            <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>
              · {formatDuration(elapsed)}
            </span>
          )}
        </span>
      </div>
      {status === 'ringing' && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onReject}
            aria-label="Reject"
            title="Reject"
            style={{
              background: 'var(--state-error)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            Reject
          </button>
          <button
            type="button"
            onClick={onAccept}
            aria-label="Accept"
            title="Accept"
            style={{
              background: 'var(--state-success)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhone size={14} style={{ transform: 'rotate(90deg)' }} />
            Accept
          </button>
        </div>
      )}
      {status === 'connected' && (
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            title={muted ? 'Unmute' : 'Mute'}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '9999px',
              background: muted ? '#fee2e2' : '#f3f4f6',
              color: muted ? '#b91c1c' : '#374151',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0
            }}
          >
            {muted ? <FaMicrophoneSlash size={16} /> : <FaMicrophone size={16} />}
          </button>

          {/* Originator-only: while the call is private, offer to upgrade it to a
              conference so other ops users can join. One-shot — once unlocked
              the icon disappears and this pill switches to conference controls. */}
          {isOriginator && !unlocked && onUnlock && (
            <button
              type="button"
              onClick={onUnlock}
              aria-label="Move call to conference"
              title="Move this call to a conference so other ops users can join"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9999px',
                background: 'var(--state-warning-bg)',
                color: 'var(--state-warning-text)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0
              }}
            >
              <MdPhoneLocked size={18} />
            </button>
          )}

          {/* Conference mode + 2+ ops users: drop only this leg; call continues. */}
          {unlocked && participantCount > 1 && onLeave && (
            <button
              type="button"
              onClick={onLeave}
              aria-label="Leave call"
              title="Leave the call (continues for everyone else)"
              style={{
                background: 'var(--surface-sunken)',
                color: 'var(--text-secondary)',
                border: '1px solid #e5e7eb',
                borderRadius: '9999px',
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              <BsFillTelephoneMinusFill size={14} />
              Leave
            </button>
          )}

          <button
            type="button"
            onClick={unlocked && onEndForEveryone ? onEndForEveryone : onEndCall}
            aria-label={unlocked ? 'End call for everyone' : 'End call'}
            title={unlocked ? 'End call for everyone' : 'End call'}
            style={{
              background: 'var(--state-error)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            {unlocked ? 'End for everyone' : 'End call'}
          </button>
        </div>
      )}
      {TERMINAL_STATUSES.has(status) && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            marginLeft: 'auto',
            background: 'var(--surface-inverse)',
            color: 'var(--text-inverse)',
            border: 'none',
            borderRadius: '9999px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0
          }}
        >
          <FaTimes size={16} color="#ffffff" />
        </button>
      )}
    </div>
  );
}

export default IncomingCallPill;
